import { LockSimple, LockSimpleOpen } from "@phosphor-icons/react";
import { createColumnHelper } from "@tanstack/react-table";

import i18n from "@/i18n";
import { MODAL_ROUTES } from "@/router";
import type { useNavigateModal } from "@/router/useNavigateModal";
import type { User } from "@/types";
import { Button } from "@/ui";
import { Badge } from "@/ui/Badge";
import { formatDateToDDMMYYYY, tw } from "@/utils";
import { mapPlanNameToLabel } from "@/utils/planUtils";

const columnHelper = createColumnHelper<User>();

export function getTableColumns(
  navigateModal: ReturnType<typeof useNavigateModal>,
) {
  const columns = [
    columnHelper.accessor("email", {
      header: "User",
      enableSorting: true,
      cell: (info) => {
        const user = info.row.original;
        const fullName =
          `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
        return (
          <div className={tw("flex flex-col", user.locked && "opacity-50")}>
            <span className="font-semibold text-neutral-900">
              {fullName || "—"}
            </span>
            <span className="text-xs text-neutral-500">{info.getValue()}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor("memberships", {
      header: i18n.t("users.memberships"),
      enableSorting: false,
      cell: (info) => {
        const plans = info.row.original.plans ?? [];
        const seen = new Set<string>();
        const uniquePlans = plans.filter((plan) => {
          if (!plan.name || seen.has(plan.name)) {
            return false;
          }
          seen.add(plan.name);
          return true;
        });

        if (uniquePlans.length === 0) {
          return (
            <span
              className={tw(
                "text-neutral-500",
                info.row.original.locked && "opacity-50",
              )}
            >
              {i18n.t("users.no_subscriptions")}
            </span>
          );
        }

        return (
          <div
            className={tw(
              "flex flex-col gap-y-1",
              info.row.original.locked && "opacity-50",
            )}
          >
            {uniquePlans.map((plan) => (
              <span
                key={plan.name}
                className="flex items-center gap-x-2 text-sm"
              >
                <span>{mapPlanNameToLabel(plan.name)}</span>
                <Badge
                  text={
                    plan.free_access
                      ? i18n.t("users.free_tag")
                      : i18n.t("users.paid_tag")
                  }
                  color={plan.free_access ? "warning" : "success"}
                />
              </span>
            ))}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "granted_materials",
      header: i18n.t("users.granted_materials"),
      cell: (info) => {
        const count = info.row.original.granted_materials_count ?? 0;
        if (count === 0) {
          return (
            <span
              className={tw(
                "text-sm text-neutral-500",
                info.row.original.locked && "opacity-50",
              )}
            >
              {i18n.t("users.materials_none")}
            </span>
          );
        }
        return (
          <span
            className={tw(
              "text-sm text-neutral-900",
              info.row.original.locked && "opacity-50",
            )}
          >
            {i18n.t("users.materials_granted_count", { count })}
          </span>
        );
      },
    }),
    columnHelper.accessor("email_verified_at", {
      header: i18n.t("users.verified_date"),
      enableSorting: true,
      cell: (info) => <span>{formatDateToDDMMYYYY(info.getValue())} </span>,
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => {
        const user = info.row.original;
        const isLocked = user.locked;
        return (
          <div className="flex gap-2">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigateModal(MODAL_ROUTES.manageUserAccessLevel, { user });
              }}
              variant="outlined"
              size="sm"
              disabled={isLocked}
            >
              {i18n.t("users.manage_access")}
            </Button>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "manageLocked",
      header: "",
      cell: (info) => {
        const user = info.row.original;
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigateModal(MODAL_ROUTES.lockUser, { user });
            }}
            variant="transparent"
            aria-label={info.row.original.locked ? "Unlock user" : "Lock user"}
            className="p-0"
          >
            {info.row.original.locked ? (
              <span className="flex size-8 items-center justify-center rounded-full bg-error-100 text-error-700 transition-colors hover:bg-error-200">
                <LockSimple size={17} weight="fill" />
              </span>
            ) : (
              <span className="flex size-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-colors hover:bg-primary-100 hover:text-primary-700">
                <LockSimpleOpen size={17} />
              </span>
            )}
          </Button>
        );
      },
    }),
  ];

  return columns;
}
